import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FORM_WEBHOOK_METHODS,
  FORM_WEBHOOK_TRIGGERS,
  FormWebhook,
  suggestWebhookRef
} from '../../../shared/dynamic-form/form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-builder-webhooks-panel',
  imports: [
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    AinFormFieldComponent,
    InfoHintComponent
  ],
  template: `
    <section class="webhooks-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.webhooks.infoTitle'"
        [descriptionKey]="'dynamic.builder.webhooks.infoDescription'"
        [bulletKeys]="['dynamic.builder.webhooks.infoBullet']" />

      <div class="toolbar">
        <strong>{{ t('dynamic.builder.webhooks.title') }}</strong>
        @if (canEdit()) {
          <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.webhooks.add')" (click)="addWebhook()"></button>
        }
      </div>

      @for (webhook of webhooks(); track webhook.ref; let idx = $index) {
        <article class="webhook-card">
          <div class="card-header">
            <strong>{{ webhook.name || webhook.ref }}</strong>
            @if (canEdit()) {
              <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeWebhook(idx)"></button>
            }
          </div>

          <ain-form-field [label]="t('dynamic.builder.webhooks.ref')">
            <input
              pInputText
              [ngModel]="webhook.ref"
              [disabled]="!canEdit()"
              (ngModelChange)="updateWebhook(idx, { ref: $event })" />
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.builder.webhooks.name')">
            <input
              pInputText
              [ngModel]="webhook.name"
              [disabled]="!canEdit()"
              (ngModelChange)="updateWebhook(idx, { name: $event })" />
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.builder.webhooks.url')">
            <input
              pInputText
              [ngModel]="webhook.url"
              [disabled]="!canEdit()"
              (ngModelChange)="updateWebhook(idx, { url: $event })" />
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.builder.webhooks.method')">
            <p-select
              [options]="methodOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="webhook.method"
              [disabled]="!canEdit()"
              appendTo="body"
              (ngModelChange)="updateWebhook(idx, { method: $event })" />
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.builder.webhooks.trigger')">
            <p-select
              [options]="triggerOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="webhook.trigger ?? ''"
              [disabled]="!canEdit()"
              [showClear]="true"
              appendTo="body"
              (ngModelChange)="updateWebhook(idx, { trigger: $event || undefined })" />
          </ain-form-field>

          <div class="headers-block">
            <div class="headers-header">
              <strong>{{ t('dynamic.builder.webhooks.headers') }}</strong>
              @if (canEdit()) {
                <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.webhooks.addHeader')" (click)="addHeader(idx)"></button>
              }
            </div>
            @for (header of headerRows(webhook); track $index; let headerIdx = $index) {
              <div class="header-row">
                <input
                  pInputText
                  [placeholder]="t('dynamic.builder.webhooks.headerKey')"
                  [ngModel]="header.key"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateHeader(idx, headerIdx, $event, header.value)" />
                <input
                  pInputText
                  [placeholder]="t('dynamic.builder.webhooks.headerValue')"
                  [ngModel]="header.value"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateHeader(idx, headerIdx, header.key, $event)" />
                @if (canEdit()) {
                  <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeHeader(idx, headerIdx)"></button>
                }
              </div>
            }
          </div>
        </article>
      } @empty {
        <p class="empty">{{ t('dynamic.builder.webhooks.empty') }}</p>
      }
    </section>
  `,
  styles: [`
    .webhooks-panel { display: grid; gap: 12px; }
    .toolbar, .headers-header, .card-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .webhook-card {
      border: 1px solid var(--surface-border);
      border-radius: 8px;
      padding: 12px;
      display: grid;
      gap: 10px;
    }
    .headers-block { display: grid; gap: 8px; }
    .header-row { display: grid; grid-template-columns: 1fr 1fr auto; gap: 8px; align-items: center; }
    .empty { margin: 0; color: var(--text-color-secondary); }
  `]
})
export class FormBuilderWebhooksPanelComponent {
  private readonly localization = inject(LocalizationService);

  readonly webhooks = input<FormWebhook[]>([]);
  readonly canEdit = input(true);

  readonly webhooksChange = output<FormWebhook[]>();

  readonly webhookRefs = computed(() => (this.webhooks() ?? []).map(item => item.ref));

  readonly methodOptions = computed(() =>
    FORM_WEBHOOK_METHODS.map(value => ({ label: value, value }))
  );

  readonly triggerOptions = computed(() =>
    FORM_WEBHOOK_TRIGGERS.map(value => ({
      label: this.t(`dynamic.builder.webhooks.trigger.${value}`),
      value
    }))
  );

  t(key: string): string {
    return this.localization.translate(key);
  }

  headerRows(webhook: FormWebhook): { key: string; value: string }[] {
    const headers = webhook.headers ?? {};
    const rows = Object.entries(headers).map(([key, value]) => ({ key, value }));
    return rows.length ? rows : [{ key: '', value: '' }];
  }

  addWebhook(): void {
    const ref = suggestWebhookRef(this.webhookRefs());
    this.webhooksChange.emit([
      ...(this.webhooks() ?? []),
      { ref, name: ref, url: '', method: 'POST', headers: {} }
    ]);
  }

  removeWebhook(index: number): void {
    this.webhooksChange.emit((this.webhooks() ?? []).filter((_, idx) => idx !== index));
  }

  updateWebhook(index: number, patch: Partial<FormWebhook>): void {
    const next = [...(this.webhooks() ?? [])];
    next[index] = { ...next[index], ...patch };
    this.webhooksChange.emit(next);
  }

  addHeader(index: number): void {
    const webhook = (this.webhooks() ?? [])[index];
    const headers = { ...(webhook.headers ?? {}), '': '' };
    this.updateWebhook(index, { headers });
  }

  updateHeader(webhookIndex: number, headerIndex: number, key: string, value: string): void {
    const webhook = (this.webhooks() ?? [])[webhookIndex];
    const rows = this.headerRows(webhook);
    rows[headerIndex] = { key, value };
    const headers: Record<string, string> = {};
    for (const row of rows) {
      if (row.key.trim()) {
        headers[row.key.trim()] = row.value;
      }
    }
    this.updateWebhook(webhookIndex, { headers });
  }

  removeHeader(webhookIndex: number, headerIndex: number): void {
    const webhook = (this.webhooks() ?? [])[webhookIndex];
    const rows = this.headerRows(webhook).filter((_, idx) => idx !== headerIndex);
    const headers: Record<string, string> = {};
    for (const row of rows) {
      if (row.key.trim()) {
        headers[row.key.trim()] = row.value;
      }
    }
    this.updateWebhook(webhookIndex, { headers: Object.keys(headers).length ? headers : {} });
  }
}
